<template>
  <div class="real-name-verification">
    <div class="verification-container">
      <AccountWalletNav />
      <div class="page-header">
        <h2>实名认证</h2>
        <p class="description">请上传您的身份证正反面照片进行实名认证</p>
      </div>

      <div v-if="loading" class="loading-mask">
        <div class="loading-spinner"></div>
        <div class="loading-text">正在获取认证数据...</div>
      </div>

      <el-alert
        v-if="auditStatus === 1"
        type="warning"
        :closable="false"
        show-icon
        title="实名认证审核中"
        class="status-alert"
      >
        已提交身份证，请等待管理员审核。通过后可申请提现与合伙人。
      </el-alert>
      <el-alert
        v-else-if="isVerified || auditStatus === 2"
        type="success"
        :closable="false"
        show-icon
        title="实名认证已通过"
        class="status-alert"
      >
        您已完成实名认证，可申请提现与臻享合伙人。
      </el-alert>
      <el-alert
        v-else-if="auditStatus === 3"
        type="error"
        :closable="false"
        show-icon
        title="实名认证未通过"
        class="status-alert"
      >
        {{ failReason || '请按要求修改后重新提交。' }}
      </el-alert>

      <el-form 
        ref="verificationFormRef" 
        :model="verificationForm" 
        :rules="rules" 
        label-width="120px"
        class="verification-form"
        :disabled="formLocked"
      >
        <el-form-item label="真实姓名" prop="realName">
          <el-input 
            v-model="verificationForm.realName" 
            placeholder="请输入您的真实姓名"
          />
        </el-form-item>

        <el-form-item label="身份证号码" prop="idCardNumber">
          <el-input 
            v-model="verificationForm.idCardNumber" 
            placeholder="请输入您的身份证号码"
            maxlength="18"
          />
        </el-form-item>

        <el-form-item label="身份证正面" prop="idCardFront">
          <el-upload
            ref="frontUpload"
            :action="uploadFrontUrl"
            :headers="uploadHeaders"
            :before-upload="beforeUpload"
            :on-success="handleFrontSuccess"
            :on-error="handleUploadError"
            :show-file-list="false"
            accept="image/*"
            class="id-card-upload"
          >
            <div class="upload-area" v-if="!verificationForm.idCardFront">
              <el-icon class="upload-icon"><Plus /></el-icon>
              <div class="upload-text">点击上传身份证正面照片</div>
            </div>
            <div class="upload-preview" v-else>
              <el-image 
                :src="`/api/v1/${verificationForm.idCardFront}`" 
                fit="cover"
                class="preview-image"
              />
              <div class="upload-overlay">
                <el-button type="primary" size="small">重新上传</el-button>
              </div>
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="身份证背面" prop="idCardBack">
          <el-upload
            ref="backUpload"
            :action="uploadBackUrl"
            :headers="uploadHeaders"
            :before-upload="beforeUpload"
            :on-success="handleBackSuccess"
            :on-error="handleUploadError"
            :show-file-list="false"
            accept="image/*"
            class="id-card-upload"
          >
            <div class="upload-area" v-if="!verificationForm.idCardBack">
              <el-icon class="upload-icon"><Plus /></el-icon>
              <div class="upload-text">点击上传身份证背面照片</div>
            </div>
            <div class="upload-preview" v-else>
              <el-image 
                :src="`/api/v1/${verificationForm.idCardBack}`" 
                fit="cover"
                class="preview-image"
              />
              <div class="upload-overlay">
                <el-button type="primary" size="small">重新上传</el-button>
              </div>
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item>
          <el-button 
            type="primary" 
            @click="submitVerification" 
            :loading="submitting"
            :disabled="!canSubmit || formLocked"
          >
            {{ formLocked ? (isVerified || auditStatus === 2 ? '已通过' : '审核中') : '提交认证' }}
          </el-button>
          <el-button @click="$router.go(-1)">取消</el-button>
        </el-form-item>
      </el-form>

      <div class="verification-tips">
        <h3>认证须知：</h3>
        <ul>
          <li>请确保身份证照片清晰可见，信息完整</li>
          <li>身份证号码必须与上传的身份证照片一致</li>
          <li>认证信息仅用于身份验证，我们将严格保护您的隐私</li>
          <li>认证通过后，您的账户将获得实名认证标识</li>
          <li>认证通过后可进行<strong>提现申请</strong>与<strong>臻享合伙人申请</strong></li>
        </ul>
        <div class="after-verify-links">
          <el-button type="primary" plain size="small" @click="router.push('/withdraw/apply')">去提现申请</el-button>
          <el-button plain size="small" @click="router.push('/referral/partner-apply')">申请合伙人</el-button>
          <el-button plain size="small" @click="router.push('/my')">返回我的</el-button>
        </div>
      </div>
      

    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { validatePhotoUpload } from '@/utils/photoUpload'
import { Plus } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import AccountWalletNav from '@/components/AccountWalletNav.vue'

export default {
  name: 'RealNameVerificationView',
  components: {
    Plus,
    AccountWalletNav
  },
  setup() {
    const router = useRouter()
    const verificationForm = ref({
      realName: '',
      idCardNumber: '',
      idCardFront: '',
      idCardBack: ''
    })
    
    const verificationFormRef = ref(null)
    const submitting = ref(false)
    const loading = ref(false)
    const auditStatus = ref(0)
    const failReason = ref('')
    const isVerified = ref(false)
    const formLocked = computed(() => auditStatus.value === 1 || isVerified.value || auditStatus.value === 2)

    const rules = {
      realName: [
        { required: true, message: '请输入真实姓名', trigger: 'blur' }
      ],
      idCardNumber: [
        { required: true, message: '请输入身份证号码', trigger: 'blur' }
      ],
      idCardFront: [
        { required: true, message: '请上传身份证正面照片', trigger: 'change' }
      ],
      idCardBack: [
        { required: true, message: '请上传身份证背面照片', trigger: 'change' }
      ]
    }

    const uploadHeaders = computed(() => ({
      'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
    }))

    const uploadFrontUrl = '/api/v1/upload/idcard/front'
    const uploadBackUrl = '/api/v1/upload/idcard/back'

    const canSubmit = computed(() => {
      return verificationForm.value.realName && 
             verificationForm.value.idCardNumber && 
             verificationForm.value.idCardFront && 
             verificationForm.value.idCardBack
    })

    // 获取已有的实名认证数据
    const getExistingVerificationData = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/idcard/verify/get', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const data = await response.json()
        
        if (data.code === 200) {
          const d = data.data || {}
          verificationForm.value.realName = d.name || ''
          verificationForm.value.idCardNumber = d.idcard || ''
          verificationForm.value.idCardFront = d.idcard_front || ''
          verificationForm.value.idCardBack = d.idcard_back || ''
          auditStatus.value = Number(d.audit_status) || 0
          failReason.value = d.fail_reason || ''
          isVerified.value = Number(d.is_verified) === 1
        }
      } catch (error) {
        console.error('Failed to get existing verification data:', error)
        // 获取失败不影响用户继续填写
      } finally {
        loading.value = false
      }
    }

    const beforeUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const handleFrontSuccess = (response) => {
      if (response.code === 200) {
        verificationForm.value.idCardFront = response.filename
        ElMessage.success('身份证正面照片上传成功')
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    const handleBackSuccess = (response) => {
      if (response.code === 200) {
        verificationForm.value.idCardBack = response.filename
        ElMessage.success('身份证背面照片上传成功')
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    const handleUploadError = (error) => {
      console.error('Upload error:', error)
      ElMessage.error('上传失败，请重试')
    }

    const submitVerification = async () => {
      if (formLocked.value) return
      try {
        submitting.value = true
        
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/idcard/verify', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            idcard_front: verificationForm.value.idCardFront,
            idcard_back: verificationForm.value.idCardBack,
            name: verificationForm.value.realName,
            idcard: verificationForm.value.idCardNumber
          })
        })

        const data = await response.json()
        
        if (data.code === 200) {
          ElMessage.success('实名认证提交成功，请等待审核')
          await getExistingVerificationData()
        } else {
          ElMessage.error(data.message || '认证提交失败')
        }
      } catch (error) {
        console.error('Verification submission error:', error)
        ElMessage.error('提交失败，请重试')
      } finally {
        submitting.value = false
      }
    }

    // 组件挂载时获取已有数据
    onMounted(() => {
      // 检查是否登录
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return
      }
      getExistingVerificationData()
    })

    return {
      router,
      verificationForm,
      verificationFormRef,
      submitting,
      loading,
      rules,
      uploadHeaders,
      uploadFrontUrl,
      uploadBackUrl,
      canSubmit,
      formLocked,
      auditStatus,
      failReason,
      isVerified,
      beforeUpload,
      handleFrontSuccess,
      handleBackSuccess,
      handleUploadError,
      submitVerification
    }
  }
}
</script>

<style scoped>
.real-name-verification {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.verification-container {
  background-color: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h2 {
  color: var(--el-text-color-primary);
  margin-bottom: 10px;
}

.description {
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.verification-form {
  margin-bottom: 30px;
}

.status-alert {
  margin-bottom: 20px;
}

.id-card-upload {
  width: 100%;
}

.upload-area {
  border: 2px dashed var(--el-border-color);
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-area:hover {
  border-color: var(--el-color-primary);
  background-color: var(--el-color-primary-light-9);
}

.upload-icon {
  font-size: 48px;
  color: var(--el-text-color-placeholder);
  margin-bottom: 10px;
}

.upload-text {
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.upload-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.preview-image {
  width: 100%;
  height: 200px;
  display: block;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.upload-preview:hover .upload-overlay {
  opacity: 1;
}

.verification-tips {
  background-color: var(--el-color-info-light-9);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.verification-tips h3 {
  color: var(--el-text-color-primary);
  margin-bottom: 15px;
  font-size: 16px;
}

.verification-tips ul {
  margin: 0;
  padding-left: 20px;
  color: var(--el-text-color-regular);
}

.verification-tips li {
  margin-bottom: 8px;
  line-height: 1.5;
}

.after-verify-links {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.loading-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #eee;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.loading-text {
  color: #409eff;
  font-size: 16px;
}

@media (max-width: 768px) {
  .real-name-verification {
    padding: 10px;
  }
  
  .verification-container {
    padding: 20px;
  }
  
  .upload-area {
    padding: 20px;
  }
  
  .preview-image {
    height: 150px;
  }
}
</style> 